// A tiny inline sparkline — the small trend line the Home stat cards carry in the mockup. Fed real
// history from GET /v1/me/trend (see HomeStatCards' activitySpark/accuracySpark) — a generic,
// dumb renderer with no opinion on where its `data` comes from. Colour comes from the wrapper via
// `currentColor`.

import { useId } from 'react';

interface SparklineProps {
  // The series to plot, oldest→newest.
  data: number[];
  className?: string;
  width?: number;
  height?: number;
}

export function Sparkline({ data, className, width = 100, height = 100 }: SparklineProps) {
  // Unique gradient id per instance — two sparklines share the page, so a fixed id would collide.
  const gradientId = useId();

  if (data.length < 2) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1; // guard a flat series (all-equal) against a divide-by-zero
  // The line occupies a band that's 50% of the box height, centred vertically — so the trend sits in
  // the middle of the tile instead of hugging the bottom.
  const band = height * 0.5;
  const top = (height - band) / 2;
  const bottom = top + band;
  const stepX = width / (data.length - 1);

  // Map each value into the band; invert y because SVG's origin is top-left.
  let lastY = bottom;
  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = top + (1 - (v - min) / span) * (bottom - top);
    lastY = y; // remember the final point so the end-dot can sit on it
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const line = points.join(' ');
  // Close the polygon down to the baseline for the faint area fill under the line.
  const area = `${line} ${width},${height} 0,${height}`;

  // The SVG stretches to fill its wrapper (preserveAspectRatio: none); vector-effect keeps the stroke
  // an even 1.5px regardless of the non-uniform scale. The end-dot is an HTML element overlaid at the
  // last point — an SVG <circle> would stretch into an ellipse under that same non-uniform scale.
  return (
    <div className={`relative h-full w-full ${className ?? ''}`}>
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true" className="h-full w-full">
        {/* Vertical fade — strongest just under the line, transparent at the baseline. */}
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity={0.22} />
            <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
          </linearGradient>
        </defs>
        <polygon points={area} fill={`url(#${gradientId})`} />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <span
        aria-hidden="true"
        className="absolute h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-current"
        style={{ left: '100%', top: `${(lastY / height) * 100}%` }}
      />
    </div>
  );
}
